// Track Enrollments - Manage student enrollments in a track
import React, { useState, useEffect } from 'react';
import { Plus, X, Users, DollarSign, AlertCircle, Check, Edit2 } from 'lucide-react';
import blocksService from '../../services/blocksService';
import studentsService from '../../services/studentsService';

const LearningBlockEnrollments = ({ blockId, track }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [editEnrollment, setEditEnrollment] = useState(null);

  const [enrollFormData, setEnrollFormData] = useState({
    studentId: '',
    paymentPlan: 'upfront',
    notes: ''
  });

  const [updateFormData, setUpdateFormData] = useState({
    status: '',
    amountPaid: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [blockId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch track details with enrollments
      const blockData = await blocksService.getBlock(blockId);
      setEnrollments(blockData.enrollments || []);

      // Fetch all students
      const studentsData = await studentsService.getStudents();
      setStudents(studentsData.students || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load enrollments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();

    if (!enrollFormData.studentId) {
      setError('Please select a student');
      return;
    }

    try {
      setError('');
      await blocksService.enrollStudent(
        blockId,
        enrollFormData.studentId,
        enrollFormData.paymentPlan,
        enrollFormData.notes
      );

      setSuccess('Student enrolled successfully!');
      setShowEnrollModal(false);
      setEnrollFormData({ studentId: '', paymentPlan: 'upfront', notes: '' });

      // Refresh enrollments
      await fetchData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error enrolling student:', err);
      setError(err.message || 'Failed to enroll student. Please try again.');
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (!editEnrollment) return;

    try {
      setError('');
      const updates = {};
      if (updateFormData.status) updates.status = updateFormData.status;
      if (updateFormData.amountPaid) updates.amountPaid = parseFloat(updateFormData.amountPaid);
      if (updateFormData.notes !== undefined) updates.notes = updateFormData.notes;

      await blocksService.updateEnrollment(blockId, editEnrollment.id, updates);

      setSuccess('Enrollment updated successfully!');
      setEditEnrollment(null);
      setUpdateFormData({ status: '', amountPaid: '', notes: '' });

      // Refresh enrollments
      await fetchData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating enrollment:', err);
      setError('Failed to update enrollment. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'withdrawn':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate total upfront price including materials and discounts
  const calculateUpfrontTotal = () => {
    if (!track) return 0;

    const baseTotal = parseFloat(block.priceUpfront || 0);
    const materialsFees = block.waiveUpfrontFees ? 0 : parseFloat(block.totalClassFees || 0);

    let discount = 0;
    if (block.upfrontDiscountType === 'percentage') {
      discount = (baseTotal + materialsFees) * (parseFloat(block.upfrontDiscountValue || 0) / 100);
    } else if (block.upfrontDiscountType === 'dollar') {
      discount = parseFloat(block.upfrontDiscountValue || 0);
    }

    let total = baseTotal + materialsFees - discount;

    // Apply rounding if enabled
    if (block.roundUpfrontPrice) {
      total = Math.round(total);
    }

    return total;
  };

  // Calculate total monthly price including materials and discounts
  const calculateMonthlyTotal = () => {
    if (!track) return 0;

    const baseTotal = parseFloat(block.priceMonthly || 0);
    const numberOfMonths = Math.max(block.numberOfMonths || 1, 1);
    const materialsFees = block.waiveMonthlyFees ? 0 : (parseFloat(block.totalClassFees || 0) / numberOfMonths);

    let discount = 0;
    if (block.monthlyDiscountType === 'percentage') {
      discount = (baseTotal + materialsFees) * (parseFloat(block.monthlyDiscountValue || 0) / 100);
    } else if (block.monthlyDiscountType === 'dollar') {
      discount = parseFloat(block.monthlyDiscountValue || 0);
    }

    let total = baseTotal + materialsFees - discount;

    // Apply rounding if enabled
    if (block.roundMonthlyPrice) {
      total = Math.round(total);
    }

    return total;
  };

  // Get students not yet enrolled
  const availableStudents = students.filter(
    s => !enrollments.some(e => e.studentId === s.id)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Track Enrollments</h2>
          <p className="text-gray-600 mt-1">
            Manage student enrollments and payment plans
          </p>
        </div>
        <button
          onClick={() => setShowEnrollModal(true)}
          className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Enroll Student
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          <p>{success}</p>
        </div>
      )}

      {/* Enrollments List */}
      {enrollments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No enrollments yet</h3>
          <p className="text-gray-600 mb-6">
            Start by enrolling students in this track
          </p>
          <button
            onClick={() => setShowEnrollModal(true)}
            className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Enroll First Student
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount Paid
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enrolled
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {enrollments.map((enrollment) => (
                <tr key={enrollment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {enrollment.studentName}
                      </div>
                      <div className="text-sm text-gray-500">{enrollment.studentEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">
                      {enrollment.paymentPlan}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(enrollment.status)}`}>
                      {enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(enrollment.amountPaid)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(enrollment.enrollmentDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setEditEnrollment(enrollment);
                        setUpdateFormData({
                          status: enrollment.status,
                          amountPaid: enrollment.amountPaid,
                          notes: enrollment.notes || ''
                        });
                      }}
                      className="text-pink-600 hover:text-pink-900"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Enroll Student Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Enroll Student</h3>
              <button
                onClick={() => {
                  setShowEnrollModal(false);
                  setEnrollFormData({ studentId: '', paymentPlan: 'upfront', notes: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEnrollSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student *
                </label>
                <select
                  value={enrollFormData.studentId}
                  onChange={(e) => setEnrollFormData({ ...enrollFormData, studentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  required
                >
                  <option value="">Select a student...</option>
                  {availableStudents.map((student) => {
                    const firstName = student.firstName || student.studentFirstName || student.student_first_name || '';
                    const lastName = student.lastName || student.studentLastName || student.student_last_name || '';
                    return (
                      <option key={student.id} value={student.id}>
                        {firstName} {lastName}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Plan *
                </label>
                <select
                  value={enrollFormData.paymentPlan}
                  onChange={(e) => setEnrollFormData({ ...enrollFormData, paymentPlan: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  required
                >
                  <option value="upfront">Upfront - {formatCurrency(calculateUpfrontTotal())} total</option>
                  <option value="monthly">Monthly - {formatCurrency(calculateMonthlyTotal())}/month</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={enrollFormData.notes}
                  onChange={(e) => setEnrollFormData({ ...enrollFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="Any special notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEnrollModal(false);
                    setEnrollFormData({ studentId: '', paymentPlan: 'upfront', notes: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg"
                >
                  Enroll Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Enrollment Modal */}
      {editEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Update Enrollment</h3>
              <button
                onClick={() => {
                  setEditEnrollment(null);
                  setUpdateFormData({ status: '', amountPaid: '', notes: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student
                </label>
                <p className="text-gray-900">{editEnrollment.studentName}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={updateFormData.status}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="withdrawn">Withdrawn</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount Paid
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={updateFormData.amountPaid}
                    onChange={(e) => setUpdateFormData({ ...updateFormData, amountPaid: e.target.value })}
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={updateFormData.notes}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditEnrollment(null);
                    setUpdateFormData({ status: '', amountPaid: '', notes: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningBlockEnrollments;
